/*
 * <quick-buy-dialog>
 *
 * One shared popup for the whole site, rendered once at the end of theme.liquid.
 * Clicking any [data-quick-buy] trigger loads `sections/mtij-quick-buy.liquid`
 * for that product and injects it here.
 *
 * Uses a native <dialog> with showModal(): the browser's top layer gives us the
 * focus trap, Escape and background inertness for free, puts the popup above the
 * cart drawer without inventing a z-index, and — crucially — never touches the
 * theme's global trapFocus(), which is single-instance and would otherwise tear
 * down the drawer's own trap.
 *
 * Triggers are bound by delegation on document because product cards are
 * destroyed and rebuilt on every collection filter (facets.js) and cart render.
 */
const QUICK_BUY_SECTION = 'mtij-quick-buy';

if (!customElements.get('quick-buy-dialog')) {
  customElements.define(
    'quick-buy-dialog',
    class QuickBuyDialog extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        this.content = this.querySelector('[data-quick-buy-content]');
        this.loading = this.querySelector('[data-quick-buy-loading]');
        this.errorPanel = this.querySelector('[data-quick-buy-error]');
        this.errorLink = this.querySelector('[data-quick-buy-error-link]');

        if (!this.dialog) return;
        if (!this.content) return;

        this.trigger = null;
        this.controller = null;

        const closeButton = this.querySelector('[data-quick-buy-close]');
        if (closeButton) closeButton.addEventListener('click', () => this.dismiss());

        // Route Escape through our own path so the trigger regains focus.
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.dismiss();
        });

        // The backdrop resolves to the dialog element itself.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dismiss();
        });

        document.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-quick-buy]');
          if (!trigger) return;
          if (!trigger.dataset.productUrl) return;
          event.preventDefault();
          this.open(trigger);
        });

        // Suivre un lien depuis la popup la ferme. Cas décisif : le lien du
        // guide des tailles pointe vers `/products/x#guide-des-tailles`, et si
        // le visiteur est DÉJÀ sur la fiche de ce produit, la navigation se
        // réduit à un changement d'ancre — la page ne recharge pas, la popup
        // resterait ouverte et le tiroir s'ouvrirait derrière elle.
        this.content.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (!link) return;
          if (link.target === '_blank') return;
          this.dismiss();
        });

        // Adding to cart from inside the popup closes it, so the cart drawer
        // that product-form.js opens right after is not stacked underneath.
        if (typeof subscribe === 'function') {
          subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
            if (this.dialog.open) this.dismiss();
          });
        }
      }

      open(trigger) {
        this.trigger = trigger;

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        this.setState('loading');
        this.load(trigger.dataset.productUrl);
      }

      load(productUrl) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        const separator = productUrl.indexOf('?') === -1 ? '?' : '&';
        const url = `${productUrl}${separator}section_id=${QUICK_BUY_SECTION}`;

        if (this.errorLink) this.errorLink.href = productUrl;

        fetch(url, { signal: this.controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          })
          .then((text) => {
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const productInfo = parsed.querySelector('product-info');
            if (!productInfo) throw new Error('no product-info');

            this.content.innerHTML = productInfo.outerHTML;
            this.runInlineScripts();
            this.setState('ready');
            this.focusContent();
          })
          .catch((error) => {
            if (error.name === 'AbortError') return;
            console.error(error);
            this.setState('error');
          });
      }

      /*
       * innerHTML never executes <script> tags. The product section ships an
       * inline one (buy-buttons' sold-out styling observer) and an external one
       * (product-size-guide.js, which a collection page has never loaded), so
       * both are re-created to run.
       *
       * JSON payloads — the variant data variant-selects reads — must NOT be
       * re-run, and external files already present must not be fetched twice.
       */
      runInlineScripts() {
        this.content.querySelectorAll('script').forEach((original) => {
          const type = original.getAttribute('type');
          const isExecutable = !type || type === 'text/javascript' || type === 'module';
          if (!isExecutable) return;

          const src = original.getAttribute('src');
          if (src) {
            const href = new URL(src, window.location.href).href;
            const alreadyLoaded = Array.from(document.querySelectorAll('script[src]')).some(
              (existing) => existing.src === href
            );
            original.remove();
            if (alreadyLoaded) return;

            const external = document.createElement('script');
            external.src = src;
            external.defer = true;
            document.head.appendChild(external);
            return;
          }

          const script = document.createElement('script');
          Array.from(original.attributes).forEach((attribute) => {
            script.setAttribute(attribute.name, attribute.value);
          });
          script.textContent = original.textContent;
          original.replaceWith(script);
        });
      }

      setState(state) {
        this.dataset.state = state;
        if (this.loading) this.loading.toggleAttribute('hidden', state !== 'loading');
        if (this.errorPanel) this.errorPanel.toggleAttribute('hidden', state !== 'error');
        this.content.toggleAttribute('hidden', state !== 'ready');
      }

      focusContent() {
        const target = this.content.querySelector('h2, [tabindex="-1"]');
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      }

      dismiss() {
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }

        if (typeof this.dialog.close === 'function' && this.dialog.open) {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }

        // Emptied on close so no second <product-info> — and no duplicate ids —
        // ever linger in the document between two openings.
        this.content.innerHTML = '';
        this.setState('idle');

        if (this.trigger && document.body.contains(this.trigger)) {
          this.trigger.focus();
        }
        this.trigger = null;
      }
    }
  );
}
